import React, { useState, useEffect } from 'react';
import { API_URL, fetchData } from '../helpers/api';
import { Calendar, ArrowRight, ChevronLeft, ChevronRight, Video } from 'lucide-react';
import FormatDate from '../utils/dateTools';
import TruncatableHtmlContent from '../utils/TruncatableHtmlContent';

const PatrimoineActualitesPage = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [articlesPerPage] = useState(9);

    useEffect(() => {
        fetchArticles();
    }, []);

    const fetchArticles = async () => {
        try {
            setLoading(true);
            const fetchedArticles = await fetchData('/api/patrimoine-actualites?populate=*');
            
            // Sort articles by publication date (most recent first)
            const sortedArticles = fetchedArticles.sort((a, b) => 
                new Date(b.publishedOn) - new Date(a.publishedOn)
            );
            
            setArticles(sortedArticles);
        } catch (error) {
            console.error('Failed to fetch patrimoine & actualités:', error);
        } finally {
            setLoading(false);
        }
    };

    // Pagination logic
    const indexOfLastArticle = page * articlesPerPage;
    const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
    const currentArticles = articles.slice(indexOfFirstArticle, indexOfLastArticle);
    const totalPages = Math.ceil(articles.length / articlesPerPage);

    const renderArticleCard = (article) => (
        <div key={article.id} className="col">
            <div className="card h-100 border-0 shadow-sm hover-translate-up transition">
                {article?.images && article.images.length > 0 && (
                    <div className="card-img-top position-relative">
                        <a href={`/patrimoine-actualites/${article.documentId}`}>
                            <img 
                                src={API_URL + article.images[0]?.url} 
                                alt={article.title} 
                                className="w-100 object-fit-cover" 
                                style={{ height: '200px' }} 
                            />
                        </a>
                        {/* Video indicator */}
                        {article.video && article.video.url && (
                            <div className="position-absolute top-0 end-0 m-2">
                                <span className="badge bg-dark rounded-pill px-2 py-1 d-flex align-items-center">
                                    <Video size={14} className="me-1" />
                                    Vidéo
                                </span>
                            </div>
                        )}
                    </div>
                )}
                <div className="card-body d-flex flex-column">
                    <div className="d-flex align-items-center text-muted small mb-2">
                        <Calendar size={14} className="me-1" />
                        <small>
                            <FormatDate dateString={article.publishedOn} format="long" />
                        </small>
                    </div>

                    <h3 className="fs-6 fw-bold mb-2 line-clamp-2">
                        <a href={`/patrimoine-actualites/${article.documentId}`} className="text-decoration-none text-dark">
                            {article.title}
                        </a>
                    </h3>
                    
                    <div className="text-muted small mb-3 flex-grow-1">
                        <TruncatableHtmlContent
                            html={article.content}
                            truncate={true}
                            maxLength={120}
                        />
                    </div>

                    <div className="mt-auto">
                        <a 
                            href={`/patrimoine-actualites/${article.documentId}`} 
                            className="text-dark fw-medium d-inline-flex align-items-center text-decoration-none"
                        >
                            Lire la suite
                            <ArrowRight size={14} className="ms-1 transition-transform event-arrow" />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPagination = () => {
        const pageNumbers = [];
        
        // Always show first page
        pageNumbers.push(1);
        
        // Add ellipsis and surrounding pages if total pages > 5
        if (totalPages > 5) {
            // Add ellipsis and surrounding pages
            if (page > 3) {
                pageNumbers.push('...');
            }
            
            // Determine range of pages to show
            const startPage = Math.max(2, page - 1);
            const endPage = Math.min(totalPages - 1, page + 1);
            
            for (let i = startPage; i <= endPage; i++) {
                if (!pageNumbers.includes(i)) {
                    pageNumbers.push(i);
                }
            }
            
            // Add ellipsis if not already near the end
            if (page < totalPages - 2) {
                pageNumbers.push('...');
            }
            
            // Always show last page
            if (!pageNumbers.includes(totalPages)) {
                pageNumbers.push(totalPages);
            }
        } else {
            // If total pages is 5 or less, show all pages
            for (let i = 2; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        }

        return pageNumbers;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-warning" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="patrimoine-actualites-page container py-4 mt-5 mb-5">
            <div className="text-center mb-5">
                <h1 className="display-4 fw-bold mb-3">
                    PATRIMOINE & <span className="text-warning">ACTUALITÉS</span>
                </h1>
                <p className="lead text-muted">
                    Découvrez notre patrimoine et suivez les dernières actualités du Mémorial ACTe
                </p>
            </div>

            {currentArticles.length === 0 ? (
                <div className="text-center py-5">
                    <p className="fs-4 text-muted">Aucun article disponible</p>
                </div>
            ) : (
                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                    {currentArticles.map(renderArticleCard)}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                    <nav aria-label="Page navigation">
                        <ul className="pagination">
                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <button 
                                    className="page-link" 
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                            </li>
                            
                            {renderPagination().map((pageNum, index) => (
                                <li 
                                    key={index} 
                                    className={`page-item ${pageNum === page ? 'active' : ''} ${pageNum === '...' ? 'disabled' : ''}`}
                                >
                                    <button 
                                        className="page-link" 
                                        onClick={() => typeof pageNum === 'number' && setPage(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                </li>
                            ))}
                            
                            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                <button 
                                    className="page-link" 
                                    onClick={() => setPage(page + 1)}
                                    disabled={page === totalPages}
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}
        </div>
    );
};

export default PatrimoineActualitesPage;